import React, { useEffect, useMemo, useState } from "react";

const API_ROOT = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");

const PENDING_LEAVE_MODULES = [2, 9, 15];

const STATUS_OPTIONS = ["Pending", "Approved", "Discuss", "Forwarded", "Rejected"];

const JOB_TYPES = [
  { value: "P", label: "Permanent" },
  { value: "C", label: "Contractual" },
  { value: "D", label: "Daily Wages" },
  { value: "M", label: "MTS" },
  { value: "O", label: "Out Sourced" },
  { value: "G", label: "Guest Faculty" },
  { value: "V", label: "Visiting" },
];

// Leave types that can't be plain-Approved by a department office; restricted set
// mirrors AjaxLeaveStatusTable.php's $restrictedTypes.
const RESTRICTED_LEAVE_TYPES = [2, 3, 4, 5, 7, 9, 13, 14, 15, 18, 19, 21];

const HFDAY_LABEL = { W: "Full Day", F: "First Half", S: "Second Half" };

function fmtDate(v) {
  if (!v) return "";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "2-digit" });
}

// Which action-dropdown options a row should offer.
// [2,15]: EmpType=Teaching -> restricted LeaveTypeIDs lose "Approve" (Forward instead);
//         EmpType=Non-Teaching -> restricted LeaveTypeIDs only regain "Forward" once NoOfDays > 120.
// [9]: always the full set.
function actionOptionsFor(moduleId, empType, row) {
  const isRestricted = RESTRICTED_LEAVE_TYPES.includes(row.LeaveTypeID);

  if ([2, 15].includes(moduleId)) {
    if (empType === "T") {
      return isRestricted
        ? ["Forwarded", "Discuss", "Rejected"]
        : ["Approved", "Discuss", "Rejected"];
    }
    // empType === "N"
    if (isRestricted) {
      return parseFloat(row.NoOfDays) > 120
        ? ["Approved", "Forwarded", "Discuss", "Rejected"]
        : ["Approved", "Discuss", "Rejected"];
    }
    return ["Approved", "Discuss", "Rejected"];
  }

  // moduleId === 9
  return ["Approved", "Forwarded", "Discuss", "Rejected"];
}

export default function PendingLeaveOffice() {
  const localUser = useMemo(() => {
    try { return JSON.parse(localStorage.getItem("user") || "null"); } catch { return null; }
  }, []);

  const moduleId = parseInt(localUser?.moduleId, 10);
  const deptId   = parseInt(localUser?.deptid, 10);
  const empId    = parseInt(localUser?.id, 10);

  const isDeptScoped = [2, 15].includes(moduleId);

  const [leaveTypes, setLeaveTypes]   = useState([]);
  const [leaveTypeID, setLeaveTypeID] = useState("All");
  const [status, setStatus]           = useState("Pending");
  const [empType, setEmpType]         = useState("T");
  const [jobType, setJobType]         = useState("P");
  const [fromDate, setFromDate]       = useState("");
  const [toDate, setToDate]           = useState("");

  const [rows, setRows]         = useState([]);
  const [searched, setSearched] = useState(false);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState("");
  const [actingID, setActingID] = useState(null);

  useEffect(() => {
    if (!PENDING_LEAVE_MODULES.includes(moduleId)) return;
    fetch(`${API_ROOT}/api/employees/pending-leave-types?moduleId=${moduleId}`)
      .then(r => r.json())
      .then(data => setLeaveTypes(Array.isArray(data) ? data : []))
      .catch(() => setLeaveTypes([]));
  }, [moduleId]);

  if (!PENDING_LEAVE_MODULES.includes(moduleId)) {
    return (
      <div className="container mt-4">
        <div className="alert alert-danger">Unauthorized access Or Session Timed out.</div>
      </div>
    );
  }

  const fetchList = async () => {
    if (isDeptScoped && !deptId) { setError("Department ID missing from session."); return; }
    if (moduleId === 9 && !empId) { setError("Employee ID missing from session."); return; }

    setError("");
    setLoading(true);
    try {
      const q = new URLSearchParams({ moduleId: String(moduleId), rStatus: status });
      if (isDeptScoped) q.set("deptId", String(deptId));
      if (moduleId === 9) q.set("empId", String(empId));
      if (fromDate) q.set("fromDate", fromDate);
      if (toDate) q.set("toDate", toDate);
      if (leaveTypeID) q.set("selectedLeaveTypeID", leaveTypeID);
      if (empType) q.set("empType", empType);
      if (jobType) q.set("jobType", jobType);

      const res = await fetch(`${API_ROOT}/api/employees/pending-leave-list?${q}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to fetch pending leave list.");
      setRows(Array.isArray(data) ? data : []);
      setSearched(true);
    } catch (err) {
      setRows([]);
      setError(err.message || "Failed to fetch pending leave list.");
    } finally {
      setLoading(false);
    }
  };

  const handleView = (e) => {
    e.preventDefault();
    fetchList();
  };

  const handleAction = async (leaveID, newStatus) => {
    if (!newStatus) return;
    setActingID(leaveID);
    setError("");
    try {
      const res = await fetch(`${API_ROOT}/api/employees/pending-leave-action`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ moduleId, empId, leaveID, status: newStatus }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to update leave status.");
      await fetchList();
    } catch (err) {
      setError(err.message || "Failed to update leave status.");
    } finally {
      setActingID(null);
    }
  };

  return (
    <div className="container-fluid mt-3">
      <h4 className="ctitle">Leave Status Admin</h4>

      {error && <div className="alert alert-danger mt-2">{error}</div>}

      <form className="row g-2 align-items-end mb-3" onSubmit={handleView}>
        <div className="col-sm-2">
          <label className="form-label">Type of Leave</label>
          <select className="form-select form-select-sm" value={leaveTypeID} onChange={e => setLeaveTypeID(e.target.value)}>
            <option value="All">All</option>
            {leaveTypes.map(t => (
              <option key={t.LeaveTypeID} value={t.LeaveTypeID}>{t.LeaveType}</option>
            ))}
          </select>
        </div>
        <div className="col-sm-1">
          <label className="form-label">Status</label>
          <select className="form-select form-select-sm" value={status} onChange={e => setStatus(e.target.value)}>
            {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div className="col-sm-2">
          <label className="form-label">Employee Type</label>
          <select className="form-select form-select-sm" value={empType} onChange={e => setEmpType(e.target.value)}>
            <option value="T">Teaching</option>
            <option value="N">Non-Teaching</option>
          </select>
        </div>
        <div className="col-sm-2">
          <label className="form-label">Job Type</label>
          <select className="form-select form-select-sm" value={jobType} onChange={e => setJobType(e.target.value)}>
            {JOB_TYPES.map(j => <option key={j.value} value={j.value}>{j.label}</option>)}
          </select>
        </div>
        <div className="col-sm-2">
          <label className="form-label">From Date</label>
          <input type="date" className="form-control form-control-sm" value={fromDate} onChange={e => setFromDate(e.target.value)} />
        </div>
        <div className="col-sm-2">
          <label className="form-label">To Date</label>
          <input type="date" className="form-control form-control-sm" value={toDate} onChange={e => setToDate(e.target.value)} />
        </div>
        <div className="col-sm-1">
          <button type="submit" className="btn btn-primary btn-sm" disabled={loading}>
            {loading ? "Loading..." : "View"}
          </button>
        </div>
      </form>

      {searched && !loading && rows.length === 0 && (
        <div className="alert alert-info">No records found for the selected criteria.</div>
      )}

      {rows.length > 0 && (
        <div className="table-responsive">
          <table className="table table-bordered table-sm" style={{ whiteSpace: "nowrap" }}>
            <thead style={{ "--bs-table-bg": "#008080", color: "white" }}>
              <tr>
                <th>SNo</th>
                <th>Name</th>
                <th>Mobile</th>
                <th>Leave Type</th>
                <th>Leave Availed</th>
                <th>From</th>
                <th>To</th>
                <th>Days</th>
                <th>Day</th>
                <th style={{ minWidth: "125px" }}>Action</th>
                <th>Progress</th>
                <th style={{ minWidth: "220px" }}>Remarks</th>
                <th>Status</th>
                <th>Entry By</th>
                <th>Print</th>
                <th>Ref. ID</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => (
                <tr key={row.LeaveID}>
                  <td>{idx + 1}</td>
                  <td>{row.EmpName}</td>
                  <td>{row.Mobile}</td>
                  <td>{row.LeaveType}</td>
                  <td>{row.LeaveAvailed}</td>
                  <td>{fmtDate(row.FromDate)}</td>
                  <td>{fmtDate(row.ToDate)}</td>
                  <td>{row.NoOfDays}</td>
                  <td>{HFDAY_LABEL[row.HFDay] || ""}</td>
                  <td>
                    <select
                      className="form-select form-select-sm"
                      defaultValue=""
                      disabled={actingID === row.LeaveID}
                      onChange={e => handleAction(row.LeaveID, e.target.value)}
                    >
                      <option value="" disabled>Status</option>
                      {actionOptionsFor(moduleId, empType, row).map(opt => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <button type="button" className="btn btn-sm btn-outline-secondary" disabled title="Coming soon">
                      Progress
                    </button>
                  </td>
                  <td>{row.Remarks}</td>
                  <td>{row.Status}</td>
                  <td>{row.EntryBy}</td>
                  <td>
                    <button type="button" className="btn btn-sm btn-outline-secondary" disabled title="Coming soon">
                      Print
                    </button>
                  </td>
                  <td>{row.LeaveID}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
